import { ChevronRight } from 'lucide-react';
import * as React from 'react';

import {
  CampaignApiError,
  TARGET_PRICE_GLOBAL_SECTOR,
  type CampaignDetailDto,
  type SeniorityLevelDto,
  type TargetPriceConfigDto,
  type TargetPriceCoupleDto,
  type TargetPriceSourceDto,
} from '@/api/campaign-client';
import {
  useApplyTargetPriceDefaults,
  useCatalogTree,
  useRemoveProfileScope,
  useSetTargetPriceConfig,
  useTargetPriceConfigs,
  useTargetPriceDefaults,
} from '@/api/use-campaigns';
import { Button } from '@/components/ui/button';
import { Panel } from '@/components/ui/panel';
import { SENIORITY_LEVELS, seniorityLevelLabel } from '@/lib/campaign-labels';
import { cn } from '@/lib/utils';
import { plural, TitleMono } from '@/routes/admin/campaigns/detail-ui';
import {
  buildPivot,
  pivotCellCount,
  pivotProfileCount,
} from '@/routes/admin/campaigns/profile-scope-model';

// =============================================================================
// target-prices-panel.tsx — Panneau « Prix cibles » du détail campagne (profils associés).
// =============================================================================
// Extrait de campaign-detail-screen.tsx (ProfileScopeSection), comportement conservé : saisie INLINE du Prix
// Cible (commit à la sortie de la cellule ou Entrée, useSetTargetPriceConfig), retrait EN BLOC de la sélection
// (useRemoveProfileScope par scopeId), repli des groupes, état vide.
//
// GATE_HUMAN 2026-09-16 (Q7) : tableau PIVOTÉ, une ligne par profil et une colonne par niveau ; les données
// restent les mêmes, seule la présentation change. Un niveau HORS périmètre (profil à 3 niveaux sur 4) est une
// cellule « — » non éditable, sans proposition. La case de ligne sélectionne TOUS les scopeId du profil.
//
// L10 (GATE_HUMAN John 2026-09-17, UC-A-044) : « cette valeur est proposée par défaut sur la base du
// référentiel Jeko. La valeur de défaut est modifiable par l'administrateur ». Chaque cellule SANS prix affiche
// la P50 Pulse proposée en gris (placeholder) ; Entrée sur la cellule vide ou le bouton « ✓ Pulse » l'accepte
// (source PulseDefault : le serveur recalcule et refuse si la valeur a changé) ; une saisie manuelle part en
// AdminEntry. Une cellule avec prix porte un badge d'origine (« Pulse » / « Saisi »). « Appliquer les valeurs
// Pulse (N) » ne pose que les couples SANS prix (jamais d'écrasement, autorité API). Campagne terminale
// (« 1. OUI, il faut bloquer. ») : carte en lecture seule. Aucune proposition (interrupteur désactivé, pas de
// P50) : rien n'est affiché, pas d'erreur. Ce panneau ne calcule ni secteur ni P50 (autorité API).
//
// Largeur : `table-fixed` à colonnes bornées, le libellé du profil passe à la ligne ⇒ aucun défilement
// horizontal à 1280 px (un conteneur défilant fermerait les menus portalés, L-079).

/** Montant groupé par milliers (« 1 000 »), espace simple (stable entre moteurs Intl). */
function formatThousands(value: number): string {
  return new Intl.NumberFormat('fr-FR').format(value).replace(/[  ]/g, ' ');
}

/** Clé d'un couple profil × niveau. */
function coupleKey(profileCode: string, seniorityLevel: SeniorityLevelDto): string {
  return `${profileCode}::${seniorityLevel}`;
}

/**
 * Mention du secteur des valeurs proposées : « P50 Pulse · secteur X », « P50 Pulse · tous secteurs », ou les
 * deux quand le repli s'est fait couple par couple. `null` si aucune proposition (rien à afficher).
 */
export function pulseSectorMention(sectors: readonly string[]): string | null {
  const distinct = [...new Set(sectors)];
  if (distinct.length === 0) {
    return null;
  }
  const named = distinct
    .filter((s) => s !== TARGET_PRICE_GLOBAL_SECTOR)
    .sort((a, b) => a.localeCompare(b, 'fr'));
  if (named.length === 0) {
    return 'P50 Pulse · tous secteurs';
  }
  const sectorPart = `${named.length > 1 ? 'secteurs' : 'secteur'} ${named.join(', ')}`;
  return distinct.includes(TARGET_PRICE_GLOBAL_SECTOR)
    ? `P50 Pulse · ${sectorPart}, repli tous secteurs`
    : `P50 Pulse · ${sectorPart}`;
}

/** Message métier d'un refus de pose (409 L6 / L6bis, 422 hors périmètre ou valeur invalide). */
export function targetPriceErrorMessage(error: Error): string {
  if (error instanceof CampaignApiError) {
    switch (error.kind) {
      case 'target-price-locked':
        return 'Campagne clôturée : prix cibles figés';
      case 'target-price-default-mismatch':
        return 'La valeur proposée par Pulse a changé : la proposition a été actualisée, vérifiez-la avant de l’accepter.';
      case 'target-price-busy':
        return 'Clôture en cours, réessayez';
      default:
        break;
    }
    if (error.status === 422) {
      return `Saisie refusée : ${error.message}`;
    }
  }
  return error.message;
}

/** Retour affiché sous le tableau : refus (alert) ou compte rendu (status). */
interface Feedback {
  readonly role: 'alert' | 'status';
  readonly text: string;
}

/**
 * Saisie INLINE du Prix Cible (€) d'un couple profil×séniorité (DBU John : « associare il Prix Cible
 * digitandolo »). État local (brouillon) ; commit au blur / Entrée si entier > 0 ET modifié (pose =
 * validation côté API, fix f765dc2). Ne mute JAMAIS à la frappe. Hors saisie, la valeur est affichée groupée
 * par milliers (« 1 000 »). L10 : `placeholder` = valeur proposée (gris) ; Entrée sur un champ VIDE appelle
 * `onAcceptSuggestion` (acceptation de la proposition).
 */
export function CibleInput({
  label,
  current,
  pending,
  onCommit,
  placeholder,
  describedBy,
  onAcceptSuggestion,
}: {
  readonly label: string;
  readonly current: number | null;
  readonly pending: boolean;
  readonly onCommit: (value: number) => void;
  readonly placeholder?: string | undefined;
  readonly describedBy?: string | undefined;
  readonly onAcceptSuggestion?: (() => void) | undefined;
}): React.JSX.Element {
  const [value, setValue] = React.useState(current === null ? '' : String(current));
  const [focused, setFocused] = React.useState(false);
  // Entrée valide PUIS retire le focus : le blur qui suit ne doit pas reposer la même valeur (double POST).
  const handledByEnter = React.useRef(false);
  // Resynchronise si la valeur serveur change hors saisie (refetch).
  React.useEffect(() => {
    setValue(current === null ? '' : String(current));
  }, [current]);

  const raw = value.replace(/[\s  ]/g, '');
  const commit = (): void => {
    const n = Number(raw);
    if (raw.length === 0 || !Number.isFinite(n) || n <= 0) {
      return;
    }
    const rounded = Math.round(n);
    if (current !== null && rounded === current) {
      return;
    }
    onCommit(rounded);
  };
  const display =
    focused || raw.length === 0 || !/^\d+$/.test(raw) ? value : formatThousands(Number(raw));

  return (
    <input
      type="text"
      inputMode="numeric"
      value={display}
      disabled={pending}
      aria-label={label}
      aria-describedby={describedBy}
      placeholder={placeholder}
      onChange={(e) => setValue(e.target.value)}
      onFocus={() => {
        handledByEnter.current = false;
        setFocused(true);
        setValue(raw);
      }}
      onBlur={() => {
        setFocused(false);
        if (handledByEnter.current) {
          handledByEnter.current = false;
          return;
        }
        commit();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          if (raw.length === 0 && current === null && onAcceptSuggestion !== undefined) {
            onAcceptSuggestion();
          } else {
            commit();
          }
          handledByEnter.current = true;
          e.currentTarget.blur();
        }
      }}
      className="h-8 w-full rounded-md border border-input bg-background px-1.5 text-right font-mono text-xs tabular-nums placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:opacity-50"
    />
  );
}

/** Badge d'origine d'un prix posé (« Pulse » / « Saisi ») ; valeur proposée en infobulle si saisie manuelle. */
function OriginBadge({ config }: { readonly config: TargetPriceConfigDto }): React.JSX.Element {
  const proposed =
    config.priceSource === 'AdminEntry' && config.defaultEurProposed !== null
      ? `proposé : ${formatThousands(config.defaultEurProposed)} €`
      : undefined;
  return (
    <span
      title={proposed}
      className="rounded-sm border border-border px-1 font-mono text-[10px] uppercase leading-4 text-muted-foreground"
    >
      <span className="sr-only">Origine : </span>
      {config.priceSource === 'PulseDefault' ? 'Pulse' : 'Saisi'}
      {proposed !== undefined ? <span className="sr-only">, {proposed}</span> : null}
    </span>
  );
}

/**
 * Cellule d'un couple AU PÉRIMÈTRE : champ de saisie (ou valeur figée en lecture seule), puis une ligne
 * d'origine (badge) ou d'acceptation de la proposition (« ✓ Pulse »). La seconde ligne est toujours rendue
 * pour garder des hauteurs de ligne homogènes.
 */
function TargetCell({
  id,
  label,
  coupleLabel,
  current,
  config,
  suggestion,
  readOnly,
  pending,
  onPose,
}: {
  readonly id: string;
  readonly label: string;
  readonly coupleLabel: string;
  readonly current: number | null;
  readonly config: TargetPriceConfigDto | undefined;
  /** Valeur proposée COURANTE, uniquement pour une cellule sans prix. */
  readonly suggestion: number | null;
  readonly readOnly: boolean;
  readonly pending: boolean;
  readonly onPose: (value: number, source: TargetPriceSourceDto) => void;
}): React.JSX.Element {
  const hintId = `${id}-hint`;
  const accept = !readOnly && current === null && suggestion !== null ? suggestion : null;
  return (
    <div className="flex flex-col items-end gap-1">
      {readOnly ? (
        <span className="flex h-8 items-center font-mono text-xs tabular-nums">
          <span className="sr-only">{label} : </span>
          {current === null ? (
            <>
              <span aria-hidden="true" className="text-muted-foreground">
                —
              </span>
              <span className="sr-only">aucun prix</span>
            </>
          ) : (
            formatThousands(current)
          )}
        </span>
      ) : (
        <CibleInput
          label={label}
          current={current}
          pending={pending}
          onCommit={(value) => onPose(value, 'AdminEntry')}
          placeholder={accept !== null ? formatThousands(accept) : undefined}
          describedBy={accept !== null ? hintId : undefined}
          onAcceptSuggestion={accept !== null ? () => onPose(accept, 'PulseDefault') : undefined}
        />
      )}
      <span className="flex min-h-4 items-center">
        {current !== null && config !== undefined ? (
          <OriginBadge config={config} />
        ) : accept !== null ? (
          <>
            <span id={hintId} className="sr-only">
              Valeur proposée par Pulse : {formatThousands(accept)} €, Entrée pour l’accepter.
            </span>
            <button
              type="button"
              disabled={pending}
              onClick={() => onPose(accept, 'PulseDefault')}
              aria-label={`Accepter la valeur Pulse de ${formatThousands(accept)} € pour ${coupleLabel}`}
              className="rounded-sm px-1 font-mono text-[10px] uppercase leading-4 text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
            >
              <span aria-hidden="true">✓ </span>Pulse
            </button>
          </>
        ) : null}
      </span>
    </div>
  );
}

export function TargetPricesPanel({
  campaign,
}: {
  readonly campaign: CampaignDetailDto;
}): React.JSX.Element {
  const campaignId = campaign.campaignId;
  const remove = useRemoveProfileScope();
  const setTarget = useSetTargetPriceConfig();
  const applyDefaults = useApplyTargetPriceDefaults();
  const targetPrices = useTargetPriceConfigs(campaignId);
  const defaults = useTargetPriceDefaults(campaignId);
  const tree = useCatalogTree();

  const groups = React.useMemo(
    () => buildPivot(campaign.profileScopes, tree.data, targetPrices.data),
    [campaign.profileScopes, tree.data, targetPrices.data],
  );
  const profileCount = pivotProfileCount(groups);
  const cellCount = pivotCellCount(groups);

  // Origine et valeur proposée mémorisée des prix posés (badge « Pulse » / « Saisi »).
  const configByKey = React.useMemo(() => {
    const map = new Map<string, TargetPriceConfigDto>();
    for (const t of targetPrices.data ?? []) {
      map.set(coupleKey(t.profileCode, t.seniorityLevel), t);
    }
    return map;
  }, [targetPrices.data]);

  // Valeurs proposées COURANTES. Interrupteur désactivé, aucune P50 ou lecture en échec ⇒ aucune (pas d'erreur).
  const suggestionByKey = React.useMemo(() => {
    const map = new Map<string, TargetPriceCoupleDto>();
    if (defaults.data?.pulseDefaultEnabled !== true) {
      return map;
    }
    for (const c of defaults.data.couples) {
      if (c.suggestedDefaultEur !== null) {
        map.set(coupleKey(c.profileCode, c.seniorityLevel), c);
      }
    }
    return map;
  }, [defaults.data]);

  const [feedback, setFeedback] = React.useState<Feedback | null>(null);
  const [lockedByRefusal, setLockedByRefusal] = React.useState(false);
  const readOnly = defaults.data?.locked === true || lockedByRefusal;
  const pending = setTarget.isPending || applyDefaults.isPending;

  const onRefused = (error: Error): void => {
    if (error instanceof CampaignApiError && error.kind === 'target-price-locked') {
      setLockedByRefusal(true);
    }
    setFeedback({ role: 'alert', text: targetPriceErrorMessage(error) });
  };

  const pose = (
    profileCode: string,
    seniorityLevel: SeniorityLevelDto,
    targetPriceEur: number,
    source: TargetPriceSourceDto,
  ): void => {
    setTarget.mutate(
      { campaignId, body: { profileCode, seniorityLevel, targetPriceEur, source } },
      { onSuccess: () => setFeedback(null), onError: onRefused },
    );
  };

  // N = couples du périmètre SANS prix pour lesquels une valeur est proposée (jamais un niveau hors périmètre).
  let applicableCount = 0;
  for (const g of groups) {
    for (const row of g.rows) {
      for (const level of SENIORITY_LEVELS) {
        const cell = row.cells[level];
        if (
          cell !== undefined &&
          cell.target === null &&
          suggestionByKey.has(coupleKey(row.profileCode, level))
        ) {
          applicableCount += 1;
        }
      }
    }
  }
  const sectorMention = pulseSectorMention(
    [...suggestionByKey.values()].map((c) => c.suggestedSector ?? TARGET_PRICE_GLOBAL_SECTOR),
  );
  const showPulse = !readOnly && sectorMention !== null;

  const onApplyDefaults = (): void => {
    applyDefaults.mutate(
      { campaignId },
      {
        onSuccess: (result) => {
          const n = result.applied.length;
          const parts = [`${String(n)} ${n > 1 ? 'valeurs Pulse appliquées' : 'valeur Pulse appliquée'}`];
          if (result.withoutDefault.length > 0) {
            parts.push(`${plural(result.withoutDefault.length, 'couple')} sans valeur proposée`);
          }
          setFeedback({ role: 'status', text: `${parts.join(' · ')}.` });
        },
        onError: onRefused,
      },
    );
  };

  // Sélection par PROFIL (retrait de tous ses scopeId) + repli des groupes.
  const [selected, setSelected] = React.useState<ReadonlySet<string>>(() => new Set());
  const [collapsed, setCollapsed] = React.useState<ReadonlySet<string>>(() => new Set());
  const toggle = (
    setter: React.Dispatch<React.SetStateAction<ReadonlySet<string>>>,
    key: string,
  ): void => {
    setter((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const removeSelected = (): void => {
    for (const g of groups) {
      for (const row of g.rows) {
        if (!selected.has(row.profileCode)) {
          continue;
        }
        for (const scopeId of row.scopeIds) {
          remove.mutate({ campaignId, entryId: scopeId });
        }
      }
    }
    setSelected(new Set());
  };

  const colSpan = 3 + SENIORITY_LEVELS.length;

  return (
    <Panel
      headingId="detail-target-prices-heading"
      title={
        <>
          Prix cibles{' '}
          <TitleMono>
            {plural(profileCount, 'profil')} · {plural(cellCount, 'cellule')}
          </TitleMono>
        </>
      }
      aside={
        <>
          {showPulse ? <span className="font-mono text-xs">{sectorMention}</span> : null}
          {showPulse && applicableCount > 0 ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={pending}
              onClick={onApplyDefaults}
            >
              {`Appliquer les valeurs Pulse (${String(applicableCount)})`}
            </Button>
          ) : null}
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={selected.size === 0 || remove.isPending}
            onClick={removeSelected}
          >
            {selected.size > 0
              ? `Retirer la sélection (${String(selected.size)})`
              : 'Retirer la sélection'}
          </Button>
        </>
      }
    >
      {campaign.profileScopes.length === 0 ? (
        <p role="status" className="px-6 py-4 text-sm text-muted-foreground">
          Aucun profil dans le périmètre.
        </p>
      ) : (
        <table className="ds-table w-full table-fixed border-collapse text-sm">
          <caption className="sr-only">
            Prix cibles des profils associés à la campagne : une ligne par profil, une colonne par niveau
            de séniorité ; « — » signale un niveau hors périmètre.
          </caption>
          <colgroup>
            <col className="w-11" />
            <col className="w-20" />
            <col />
            {SENIORITY_LEVELS.map((level, i) => (
              <col
                key={level}
                className={i === SENIORITY_LEVELS.length - 1 ? 'w-24' : 'w-[4.5rem]'}
              />
            ))}
          </colgroup>
          <thead>
            <tr className="border-b border-border bg-muted/40 text-left">
              <th scope="col" className="py-3 pl-6 pr-1">
                <span className="sr-only">Sélection</span>
              </th>
              <th scope="col" className="px-2 py-3">
                Code
              </th>
              <th scope="col" className="px-2 py-3">
                Profil
              </th>
              {SENIORITY_LEVELS.map((level, i) => (
                <th
                  key={level}
                  scope="col"
                  className={cn(
                    'py-3 pl-1 text-right !text-[10px] !tracking-normal',
                    i === SENIORITY_LEVELS.length - 1 ? 'pr-6' : 'pr-1',
                  )}
                >
                  {seniorityLevelLabel(level)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {groups.map((group) => {
              const open = !collapsed.has(group.key);
              return (
                <React.Fragment key={group.key}>
                  <tr>
                    <th scope="colgroup" colSpan={colSpan} className="bg-muted/20 p-0 text-left">
                      <button
                        type="button"
                        aria-expanded={open}
                        onClick={() => toggle(setCollapsed, group.key)}
                        className="flex w-full items-center gap-2 px-6 py-2 text-left text-sm font-medium text-foreground hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
                      >
                        <ChevronRight
                          aria-hidden="true"
                          className={cn(
                            'size-4 shrink-0 text-muted-foreground transition-transform',
                            open ? 'rotate-90' : undefined,
                          )}
                        />
                        {group.famille} › {group.sousFamille}
                      </button>
                    </th>
                  </tr>
                  {open
                    ? group.rows.map((row) => {
                        const isSelected = selected.has(row.profileCode);
                        return (
                          <tr
                            key={row.profileCode}
                            className={cn(
                              'border-t border-border',
                              isSelected ? 'bg-dash-band' : undefined,
                            )}
                          >
                            <td className="py-3.5 pl-6 pr-1 align-middle">
                              <input
                                type="checkbox"
                                className="size-4 rounded border-input text-primary focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                                checked={isSelected}
                                onChange={() => toggle(setSelected, row.profileCode)}
                                aria-label={`Sélectionner ${row.libelle} (${row.profileCode}) pour retrait`}
                              />
                            </td>
                            <td className="break-all px-2 py-3.5 align-middle font-mono text-xs">
                              {row.profileCode}
                            </td>
                            <td className="break-words px-2 py-3.5 align-middle">{row.libelle}</td>
                            {SENIORITY_LEVELS.map((level, i) => {
                              const cell = row.cells[level];
                              const sen = seniorityLevelLabel(level);
                              const key = coupleKey(row.profileCode, level);
                              return (
                                <td
                                  key={level}
                                  className={cn(
                                    'py-2 pl-1 text-right align-middle',
                                    i === SENIORITY_LEVELS.length - 1 ? 'pr-6' : 'pr-1',
                                  )}
                                >
                                  {cell === undefined ? (
                                    <>
                                      <span aria-hidden="true" className="text-muted-foreground">
                                        —
                                      </span>
                                      <span className="sr-only">{sen} hors périmètre</span>
                                    </>
                                  ) : (
                                    <TargetCell
                                      id={`target-${row.profileCode}-${level}`}
                                      label={`Prix Cible de ${row.libelle} (${sen}) en euros`}
                                      coupleLabel={`${row.libelle} (${sen})`}
                                      current={cell.target}
                                      config={configByKey.get(key)}
                                      suggestion={suggestionByKey.get(key)?.suggestedDefaultEur ?? null}
                                      readOnly={readOnly}
                                      pending={pending}
                                      onPose={(value, source) =>
                                        pose(row.profileCode, level, value, source)
                                      }
                                    />
                                  )}
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })
                    : null}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      )}

      {feedback !== null ? (
        <p
          role={feedback.role}
          className={cn(
            'border-t border-border px-6 py-3 text-sm',
            feedback.role === 'alert' ? 'text-destructive' : 'text-muted-foreground',
          )}
        >
          {feedback.text}
        </p>
      ) : null}
      {readOnly && !lockedByRefusal ? (
        <p role="status" className="border-t border-border px-6 py-3 text-sm text-muted-foreground">
          Campagne clôturée : prix cibles figés
        </p>
      ) : null}

      <p className="border-t border-border px-6 py-3 text-xs text-muted-foreground">
        {readOnly
          ? '€ HT / jour · lecture seule.'
          : `€ HT / jour · enregistré à la sortie de la cellule. Les profils ajoutés depuis le catalogue apparaissent ici avec des cellules vides.${
              showPulse ? ' Valeur grise = proposition Pulse : Entrée ou « ✓ Pulse » pour l’accepter.' : ''
            }`}
      </p>
    </Panel>
  );
}
